'use client';

import Link from 'next/link';
import JsonLd from '@/components/JsonLd';
import IndustryCrossLinks from "@/components/landing/IndustryCrossLinks";
import ResearchBacking from "@/components/landing/ResearchBacking";
import Footer from "@/components/shared/Footer";
import PublicHeader from "@/components/shared/PublicHeader";

const realEstateResearch = [
  {
    text: "96% of home buyers relied on online resources during their property search in 2024.",
    source: "NAR Profile of Home Buyers & Sellers 2024",
    url: "https://www.nar.realtor/research-and-statistics",
  },
  {
    text: "The agent who responds first wins the lead 78% of the time — most inquiries come outside business hours.",
    source: "NAR Real Estate in a Digital Age Report 2024",
    url: "https://www.nar.realtor/research-and-statistics",
  },
  {
    text: "Real estate agents who respond to web leads within 5 minutes are 21x more likely to convert them into clients.",
    source: "Real Trends / Tom Ferry Research 2024",
    url: "https://www.tomferry.com/",
  },
];

const realEstateFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is an AI assistant for real estate agents?",
      acceptedAnswer: { "@type": "Answer", text: "An AI assistant for real estate agents is a chatbot trained on your listings, availability, and booking procedures. It responds to property enquiries instantly, 24/7, answering questions about listings and scheduling viewings automatically." },
    },
    {
      "@type": "Question",
      name: "How does AI lead qualification work for estate agents?",
      acceptedAnswer: { "@type": "Answer", text: "The AI uses retrieval-augmented generation (RAG) to answer questions from your uploaded property data and business information. When a lead expresses interest, the AI collects their details and books viewings directly. It only shares information you have explicitly provided." },
    },
    {
      "@type": "Question",
      name: "What are the limitations of AI agents for real estate?",
      acceptedAnswer: { "@type": "Answer", text: "AI agents cannot provide property valuations, legal advice, or financial guidance. They are designed for structured tasks like answering listing questions, scheduling viewings, and qualifying leads. Complex negotiations and professional advice are always handled by your team." },
    },
  ],
};

const realEstateSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Aditya Labs AI Lead Qualifier for Real Estate Agents",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "AI agent that responds to property inquiries within seconds, answers questions about listings, and books viewings 24/7.",
  url: "https://adityalabs.ai/real-estate",
  audience: { "@type": "BusinessAudience", audienceType: "Real Estate Agents and Brokerages" },
  provider: { "@type": "Organization", name: "Aditya Labs", url: "https://adityalabs.ai" },
  offers: { "@type": "Offer", price: "49", priceCurrency: "USD", description: "Plans from $49/month" },
};

export default function RealEstateLandingPage() {
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans overflow-x-hidden selection:bg-blue-500/30">
      <JsonLd data={realEstateSchema} />
      <JsonLd data={realEstateFaqSchema} />

      {/* NAVBAR */}
      <PublicHeader variant="industry" accentColor="blue" maxWidth="max-w-7xl" />

      {/* HERO SECTION */}
      <section className="relative pt-16 pb-24 lg:pt-32 lg:pb-40 overflow-hidden">

        {/* Ambient Background Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[120px] -z-10 opacity-60"></div>

        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT COLUMN: COPY */}
          <div className="space-y-10 relative z-10">

            {/* Urgency Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold tracking-wider uppercase animate-pulse">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              Alert: You missed 3 leads while sleeping
            </div>

            {/* Headline */}
            <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight tracking-tight">
              Every Missed Lead <br />
              Costs You <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">A Commission.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg lg:text-xl text-gray-400 max-w-lg leading-relaxed">
              Stop losing commissions to speed. Our AI agent texts leads quickly, answers questions about square footage, and books viewings 24/7.
              <br className="hidden lg:block" /><br className="hidden lg:block" />
              You only wake up to sign the deal.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/signup" className="flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl shadow-blue-900/40 transition-all transform hover:-translate-y-1">
                Deploy My Agent
              </Link>
              <Link href="/demo#real-estate" className="flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-4 rounded-xl font-medium backdrop-blur-sm transition-all">
                View Live Demo
              </Link>
            </div>

            {/* Trust/Guarantee */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 text-sm text-gray-500 dark:text-gray-400 pt-6 border-t border-gray-800/50">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span className="font-medium text-gray-300">&ldquo;First Viewing&rdquo; Guarantee</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                <span className="font-medium text-gray-300">Fair Housing Compliant</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 max-w-md">Book at least one viewing in your first 30 days, or we extend your trial free. Upload your listings or paste from MLS — the AI learns your inventory in minutes.</p>
            </div>
          </div>

          {/* RIGHT COLUMN: VISUAL PROOF (Phone Mockup) */}
          <div className="relative mx-auto lg:mx-0 w-full max-w-md">

            {/* Glass Container */}
            <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-[2.5rem] border border-gray-700/50 shadow-2xl backdrop-blur-xl relative z-10">
              <div className="bg-black rounded-[2rem] overflow-hidden border border-gray-800 h-[650px] flex flex-col relative">

                {/* Status Bar */}
                <div className="bg-gray-900/80 backdrop-blur-md p-4 flex items-center justify-between border-b border-gray-800 sticky top-0 z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs font-bold shadow-lg">AI</div>
                    <div>
                      <div className="font-semibold text-white text-sm">Real Estate Assistant</div>
                      <div className="text-[10px] text-green-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                        Active Now
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 p-5 space-y-6 overflow-y-auto bg-gray-950 scrollbar-hide">
                  <div className="text-center text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-widest my-4">Today 2:42 AM</div>

                  {/* Message 1 (Lead) */}
                  <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      Hi, I saw the listing on Oak Street. Is it still available? I work late shifts.
                    </div>
                  </div>

                  {/* Message 2 (AI) */}
                  <div className="flex justify-start">
                    <div className="bg-blue-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg shadow-blue-900/20">
                      Yes! 12 Oak St is available. It just hit the market. It has 4 beds, 3 baths, and a finished basement.
                    </div>
                  </div>

                  {/* Message 3 (AI) */}
                  <div className="flex justify-start">
                    <div className="bg-blue-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg shadow-blue-900/20">
                      Since you work late, I can schedule a viewing for you tomorrow at 11 AM or 1 PM? 🏠
                    </div>
                  </div>

                  {/* Message 4 (Lead) */}
                  <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      11 AM works perfect. Thanks for the quick reply!
                    </div>
                  </div>

                  {/* Conversion Notification */}
                  <div className="flex justify-center my-6">
                    <div className="bg-green-500/10 text-green-400 text-xs font-semibold px-4 py-1.5 rounded-full border border-green-500/20 flex items-center gap-2">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                      Viewing Booked &amp; Synced to Calendar
                    </div>
                  </div>
                </div>

                {/* Input Area (Visual) */}
                <div className="p-4 bg-gray-900 border-t border-gray-800">
                  <div className="h-12 bg-gray-800 rounded-full w-full opacity-50 border border-gray-700"></div>
                </div>
              </div>
            </div>

            {/* Back Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-blue-500/20 blur-[90px] -z-10 rounded-full"></div>
          </div>
        </div>
      </section>
      {/* Projected Impact */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">How AI Helps Real Estate Agents</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-blue-400 mb-2">Never Miss a Late-Night Lead</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                AI responds to inquiries 24/7 — answering listing questions and booking viewings even at 2 AM. No more lost leads from delayed responses.
              </p>
              <p className="text-xs text-gray-500">We are onboarding our first real estate agents now</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-green-400 mb-2">Instant Listing Knowledge</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                Upload your listings and the AI learns square footage, bedrooms, pricing, and neighbourhood details in minutes. Answers buyer questions instantly.
              </p>
              <p className="text-xs text-gray-500">Setup takes under an hour for most teams</p>
            </div>
          </div>
          <p className="text-center text-xs text-gray-600 mt-6">AI never discriminates based on protected classes. All interactions comply with Fair Housing Act requirements.</p>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-10 px-6">
        <div className="max-w-2xl mx-auto text-center bg-blue-900/20 border border-blue-700/30 rounded-2xl p-8">
          <p className="text-white font-semibold text-lg mb-2">Plans start at $49/month</p>
          <p className="text-gray-400 text-sm mb-4">One closed deal pays for a decade of subscriptions.</p>
          <Link href="/pricing" className="text-blue-400 hover:text-blue-300 text-sm font-medium underline">View all pricing plans →</Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[...realEstateFaqSchema.mainEntity,
              { name: "Can it pull listings from MLS automatically?", acceptedAnswer: { text: "Currently, you upload listing details via CSV, paste from your MLS sheet, or enter them manually — the AI learns your inventory in minutes. We are building direct IDX/RETS feed integration for automatic listing sync, expected Q3 2026. In the meantime, updating a listing takes under 30 seconds from your dashboard." } },
              { name: "How does it handle buyer matching?", acceptedAnswer: { text: "When a lead inquires about a specific property, the AI captures their preferences (beds, baths, budget, location, timeline). If that listing doesn't fit, the AI suggests other listings from your inventory that match. Your dashboard shows all captured buyer preferences so you can proactively reach out with new listings." } },
            ].map((faq: { name: string; acceptedAnswer: { text: string } }, i: number) => (
              <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-3">{faq.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ResearchBacking stats={realEstateResearch} />
      <IndustryCrossLinks current="real-estate" />
      <Footer />
    </div>
  );
}
